import { Rack } from './merchandisingTypes';
import { safeSinsangUrl } from './RackOrderLinks';

const FIXTURE_IDS = ['pants_a', 'pants_b', 'tops_left', 'tops_right', 'accessories', 'mdoc'];
export interface SourceLookupRack { id: string; items: { sku: string; variants: never[] }[] }
export interface SourceLookup { racks: SourceLookupRack[]; error: string }

/** Empty variants mean product-page inspection, never a fabricated purchase quantity. */
export function buildDailySourceLookup(racks: Rack[]): SourceLookup {
  const grouped = new Map<string, Set<string>>();
  const seen = new Set<string>();
  for (const rack of racks) {
    if (!FIXTURE_IDS.includes(rack.id)) return { racks: [], error: '衣架資料不完整，請重新整理配置。' };
    const skus = grouped.get(rack.id) || new Set<string>();
    grouped.set(rack.id, skus);
    for (const item of rack.items) {
      const sku = String(item.sku).trim();
      if (!/^[A-Za-z0-9_-]{1,100}$/.test(sku)) return { racks: [], error: '商品編號格式不正確，請重新整理配置。' };
      if (seen.has(sku)) continue;
      seen.add(sku); skus.add(sku);
      if (seen.size > 500 || skus.size > 300) return { racks: [], error: '本頁商品超過貨源查詢上限，請縮小配置範圍。' };
    }
  }
  return { racks: Array.from(grouped, ([id, skus]) => ({ id, items: Array.from(skus).sort().map((sku) => ({ sku, variants: [] as never[] })) }))
    .filter((rack) => rack.items.length).sort((a, b) => a.id.localeCompare(b.id)), error: '' };
}

export function readDailySourceLinks(data: unknown, racks: SourceLookupRack[]): Record<string, string[]> {
  const result: Record<string, string[]> = Object.create(null);
  const requested = new Map(racks.map((rack) => [rack.id, new Set(rack.items.map((item) => item.sku))]));
  racks.forEach((rack) => rack.items.forEach((item) => { result[item.sku] = []; }));
  if (!data || typeof data !== 'object') return result;
  const rows = (data as { racks?: unknown }).racks;
  if (!Array.isArray(rows)) return result;
  rows.forEach((rack) => {
    const allowed = requested.get(rack?.id);
    if (!allowed || !Array.isArray(rack.items)) return;
    rack.items.forEach((item: { sku?: unknown; links?: unknown }) => {
      const sku = String(item?.sku ?? '');
      if (!allowed.has(sku) || !Array.isArray(item.links)) return;
      const safe = item.links.filter((url): url is string => typeof url === 'string' && url.length <= 2048
        && !Array.from(url).some((char) => char.charCodeAt(0) <= 32 || char.charCodeAt(0) === 127))
        .map(safeSinsangUrl).filter((url): url is string => !!url);
      result[sku] = Array.from(new Set([...result[sku], ...safe]));
    });
  });
  return result;
}

export function primaryDailyLinks(skus: string[], links: Record<string, string[]>): string[] {
  return Array.from(new Set(skus.map((sku) => links[sku]?.[0]).filter((url): url is string => !!url)));
}

/** Called only inside the user's click handler. Stop at the first blocked tab. */
export function openDailySourceBatch(urls: string[], openTab: () => Window | null): { opened: string[]; blocked: boolean } {
  const opened: string[] = [];
  for (const url of urls.slice(0, 10)) {
    if (safeSinsangUrl(url) !== url) continue;
    let tab: Window | null = null;
    try {
      tab = openTab();
      if (!tab) return { opened, blocked: true };
      tab.opener = null;
      tab.location.replace(url);
      opened.push(url);
    } catch {
      try { tab?.close(); } catch { /* A blocked tab may already be inaccessible. */ }
      return { opened, blocked: true };
    }
  }
  return { opened, blocked: false };
}
